import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  BuildingStorefrontIcon,
  CheckIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import LandingMap from './LandingMap';
import { VISUAL } from './layout';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const Counter: React.FC<{ target: number }> = ({ target }) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return undefined;
    let start = 0;
    let frame = 0;
    const step = (now: number) => {
      if (!start) start = now;
      const p = Math.min((now - start) / 1600, 1);
      setValue(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    // Background tabs throttle rAF; make sure the final value always lands.
    const settle = window.setTimeout(() => setValue(target), 2200);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
    };
  }, [inView, target]);

  return <span ref={ref}>{value}</span>;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`lp-eyebrow ${className}`}>{children}</p>
);

const STATS = [
  { value: 140, suffix: '+', label: 'Countries served' },
  { value: 18, suffix: '+', label: 'Years of experience' },
  { value: 99, suffix: '%', label: 'Success rate' },
];

const ACCREDITATIONS = [
  { name: 'IATA', file: 'iata-logo.png' },
  { name: 'FIATA', file: 'fiata-logo-300dpi.jpg' },
  { name: 'Singapore Logistics Association', file: 'singapore-logistic.png' },
  { name: 'bizSAFE', file: 'bizsafe.png' },
  { name: 'ISO 14001', file: 'iso-14001.svg' },
  { name: 'Cyber Essentials Mark', file: 'cem.svg' },
];

const SERVICES = [
  {
    n: '01',
    eyebrow: 'Clear the way',
    title: 'Trade compliance',
    body: 'Cross borders with confidence. IOR, EOR and customs expertise for your ICT hardware.',
    icon: ShieldCheckIcon,
    to: '/services/cross-border-compliance',
    featured: false,
  },
  {
    n: '02',
    eyebrow: 'Connect your markets',
    title: 'Global transport & freight',
    body: 'Keep your business moving with secure, coordinated air, ocean and land freight.',
    icon: PaperAirplaneIcon,
    to: '/services/global-transport-freight',
    featured: true,
  },
  {
    n: '03',
    eyebrow: "Ready for what's next",
    title: 'Warehousing & fulfilment',
    body: 'Store, clear and deliver. Put your inventory exactly where your business needs it.',
    icon: BuildingStorefrontIcon,
    to: '/services/warehousing-fulfilment',
    featured: false,
  },
  {
    n: '04',
    eyebrow: 'Complete the circle',
    title: 'Value added services',
    body: 'Reverse logistics, RMA, kitting, pick and pack. Get more from every device, at every stage.',
    icon: ArrowPathIcon,
    to: '/contact-us',
    featured: false,
  },
];

// /site3 adds a section on the Singapore flagship warehouse and uses its photos.
const IS_WAREHOUSE = VISUAL === 'warehouse';
const PHOTO_DIR = `${process.env.PUBLIC_URL}/assets/images/warehouse`;

const WAREHOUSE_FACTS = [
  'Fast clearance (max 15 minutes)',
  'Loading onto the aircraft within 6 hours',
  'NFO (next flight out)',
];

const WAREHOUSE_PHOTOS = [
  { file: 'changi-ftz-racking.jpg', caption: 'Pallet racking for networking equipment' },
  { file: 'changi-ftz-forklifts.jpg', caption: 'Our own forklift fleet' },
  { file: 'changi-ftz-airside-door.jpg', caption: 'Bay door facing the airfield' },
];

const DIFFERENCE_PHOTO = IS_WAREHOUSE
  ? { file: 'changi-ftz-loading-bay.jpg', alt: 'A truck at the loading bay of the TrueLog Changi FTZ warehouse' }
  : { file: 'changi-ftz-airside.jpg', alt: 'Inside the TrueLog flagship warehouse at Changi Airport, looking out to the airfield' };

const DIFFERENCE_POINTS = [
  'One point of contact, across borders',
  'Local knowledge. Global coordination.',
  'Support across the entire device lifecycle',
];

const TESTIMONIAL = {
  quote:
    'I was very impressed with the whole process and all of the staff that were involved behind the scenes in making all the deliveries run so smoothly.',
  author: 'Melissa J. T',
  role: 'TrueLog customer',
};

const LandingSections: React.FC = () => (
  <div className="relative z-[1]">
    {/* reach + accreditations */}
    <section id="lp-stats" className={`scroll-mt-20 text-white ${IS_WAREHOUSE ? '' : 'border-t border-white/[0.08]'}`}>
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
        <div className="grid gap-8 py-10 lg:grid-cols-[1fr_2fr] lg:items-center lg:py-12">
          <Reveal>
            <p className="lp-h3 text-[1.1rem]">Big on reach.</p>
            <p className="lp-h3 text-[1.1rem] !text-white/50">Personal in approach.</p>
          </Reveal>
          <div className="grid grid-cols-3 divide-x divide-white/10">
            {STATS.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.08} className={i === 0 ? '' : 'pl-6 sm:pl-10'}>
                <p className="lp-h2 text-[2.2rem] sm:text-[2.6rem]">
                  <Counter target={stat.value} />
                  <span className="align-top text-[1.3rem] text-[#8fd3f4]">{stat.suffix}</span>
                </p>
                <p className="mt-1 text-xs text-white/50">{stat.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="grid gap-5 border-t border-white/[0.08] py-6 lg:grid-cols-[1fr_2fr] lg:items-center">
          <Eyebrow>Accreditations &amp; memberships</Eyebrow>
          <div className="flex flex-wrap items-center gap-3">
            {ACCREDITATIONS.map((logo) => (
              <div
                key={logo.name}
                title={logo.name}
                className="flex h-12 items-center justify-center rounded-xl bg-white/[0.92] px-3.5 transition-transform hover:-translate-y-0.5"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/partners/${logo.file}`}
                  alt={logo.name}
                  loading="lazy"
                  className="h-7 w-auto max-w-[110px] object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* 01 / what we do */}
    <section className="pt-16 text-white lg:pt-24">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
        <Reveal>
          <Eyebrow>01 / What we do</Eyebrow>
        </Reveal>
        <div className="mt-5 grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          <Reveal>
            <h2 className="lp-h2">
              Every move.
              <br />
              <span className="text-white/45">Connected.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:pb-1">
            <p className="max-w-sm text-[15px] leading-7 text-white/65">
              ICT logistics has a lot of moving parts. We bring them together, so you can move forward.
            </p>
            <Link to="/services" className="lp-link mt-4">
              All our solutions
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {SERVICES.map((item, i) => (
            <Reveal key={item.n} delay={i * 0.07} className="h-full">
              <Link
                to={item.to}
                className={`lp-card group flex h-full flex-col p-6 ${item.featured ? 'lp-card--featured xl:-translate-y-2' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <item.icon className="h-5 w-5 text-[#8fd3f4]" />
                  <span className="text-[11px] font-semibold text-white/40">{item.n}</span>
                </div>
                <Eyebrow className="mt-9">{item.eyebrow}</Eyebrow>
                <h3 className="lp-h3 mt-2">{item.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-6 text-white/60">{item.body}</p>
                <span className="mt-6 inline-flex h-8 w-8 items-center justify-center self-end rounded-full border border-white/15 bg-white/[0.06] text-white/70 transition-colors group-hover:border-[#0E9ED5] group-hover:text-white">
                  <ArrowUpRightIcon className="h-3.5 w-3.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {IS_WAREHOUSE && (
      <section className="pt-16 text-white lg:pt-24">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>02 / Our Singapore flagship</Eyebrow>
          </Reveal>
          <div className="mt-5 grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-end">
            <Reveal>
              <h2 className="lp-h2">
                Bonded at Changi.
                <br />
                <span className="text-white/45">Minutes from the aircraft.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:pb-1">
              <ul className="space-y-2.5">
                {WAREHOUSE_FACTS.map((fact) => (
                  <li key={fact} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#8fd3f4]" />
                    {fact}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-5 text-white/45">
                7 Airline Road, #02-17 &amp; #02-18, Cargo Agents Building E, Singapore 819834
              </p>
              <Link to="/services/warehousing-fulfilment" className="lp-link mt-4">
                Warehousing &amp; fulfilment
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4">
            {WAREHOUSE_PHOTOS.map((photo, i) => (
              <Reveal key={photo.file} delay={i * 0.07}>
                <figure className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)]">
                  <img
                    src={`${PHOTO_DIR}/${photo.file}`}
                    alt={photo.caption}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-3 text-[11px] text-white/85 sm:p-4 sm:text-xs">
                    {photo.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* 02 (03 on /site3) / the TrueLog difference */}
    <section className="py-16 text-white lg:py-24">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-14">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)]">
              <img
                src={`${PHOTO_DIR}/${DIFFERENCE_PHOTO.file}`}
                alt={DIFFERENCE_PHOTO.alt}
                loading="lazy"
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/15 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6 text-white">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">From complexity</p>
                  <p className="lp-h3 mt-1">To complete confidence.</p>
                </div>
                <Link
                  to="/why-truelog"
                  className="rounded-full border border-white/30 p-2 transition-colors hover:bg-white/10"
                  aria-label="Why TrueLog"
                >
                  <ArrowUpRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>{IS_WAREHOUSE ? '03' : '02'} / The TrueLog difference</Eyebrow>
            <h2 className="lp-h2 mt-4">
              Your ambition.
              <br />
              Our expertise.
              <br />
              <span className="text-white/45">One team.</span>
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-7 text-white/65">
              New markets shouldn&apos;t mean more complexity. We bring your logistics, compliance and ICT
              operations together under one accountable partner.
            </p>
            <ul className="mt-6 space-y-2.5">
              {DIFFERENCE_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#8fd3f4]" />
                  {point}
                </li>
              ))}
            </ul>
            <Link to="/about-us" className="lp-link mt-7">
              Get to know TrueLog
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    {/* testimonial */}
    <section className="pb-16 text-white lg:pb-24">
      <div className="lp-card mx-auto max-w-[1180px] px-6 py-10 sm:px-10 lg:py-12">
        <Reveal>
          <Eyebrow>Trust is built. Shipment by shipment.</Eyebrow>
        </Reveal>
        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_1.5fr]">
          <Reveal>
            <h2 className="lp-h2 text-[1.75rem] sm:text-[2rem]">
              Good people.
              <br />
              <span className="text-white/45">Great partnerships.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="text-lg leading-8 text-white/85">&ldquo;{TESTIMONIAL.quote}&rdquo;</blockquote>
            <p className="mt-4 text-xs text-white/50">
              <span className="font-semibold text-white/80">{TESTIMONIAL.author}</span>
              <span className="mx-2">&middot;</span>
              {TESTIMONIAL.role}
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <LandingMap />
  </div>
);

export default LandingSections;
